import { Icon } from './Icon';
import { useTranslation } from '../i18n';
import { COLORS } from '../lib/theme';

/**
 * CLUB-576: the map editor's shared action footer — Cancel and Done, nothing else.
 *
 * It used to be a slot bag. Delete sat in it, Undo sat in it, and where they sat
 * drifted per tool: the four roomy panels pushed Delete onto a band of its own
 * while Line — the densest panel, with a thickness slider and a comment field —
 * gave up and moved it inline. That was the tell that a slotted footer degrades
 * as panels grow, so the slots are gone:
 *
 *   Delete → a labelled `Delete {type}` row, last in the panel body
 *   Undo   → the map's own control stack, one implementation for every mode
 *
 * What remains is constant. No panel can crowd it, Line stops needing a special
 * case, and Done can flex to fill the width left over — which is also what
 * absorbs long localised labels (German and French are the tight ones).
 */
export function MapToolActions({
  onCancel,
  onDone,
  doneDisabled,
  doneAccent,
  variant = 'footer',
}: {
  onCancel: () => void;
  onDone: () => void;
  doneDisabled?: boolean;
  /** Tints Done to the layer's accent (sub-areas, forbidden areas). */
  doneAccent?: string;
  /**
   * `footer` sits at the bottom of a HUD card (top rule, own padding);
   * `inline` sits inside an existing row, such as the pin-placement pill.
   */
  variant?: 'footer' | 'inline';
}) {
  const { t } = useTranslation('map');
  const footer = variant === 'footer';
  // 42px of button inside a 44px target — the row's padding supplies the rest.
  const height = 42;
  return (
    <div
      // Marks the shared row wherever it lands, so a test can assert on a
      // tool's own controls rather than on the editor's top-bar Cancel.
      data-map-tool-actions=""
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        ...(footer
          ? { padding: '10px 12px', borderTop: `1px solid ${COLORS.surfaceContainerHighest}` }
          : null),
      }}
    >
      <button
        className="wh-btn wh-btn-text"
        onClick={onCancel}
        // Fixed: Cancel is the same size in every panel, and Done takes the rest.
        // 104px because German's "Abbrechen" measures 73px of the 84px this
        // leaves for text — 96px left it 3px from wrapping. `nowrap` is the
        // backstop if a font substitution ever eats the rest.
        style={{
          minHeight: height,
          width: 104,
          flex: 'none',
          justifyContent: 'center',
          whiteSpace: 'nowrap',
        }}
      >
        {t('toolActions.cancel')}
      </button>
      <button
        className="wh-btn wh-btn-primary"
        onClick={onDone}
        disabled={doneDisabled}
        style={{
          minHeight: height,
          flex: 1,
          justifyContent: 'center',
          ...(doneAccent ? { background: doneAccent, borderColor: doneAccent } : null),
        }}
      >
        <Icon name="check" size={14} />
        {t('toolActions.done')}
      </button>
    </div>
  );
}
